// Client-side routing for the hero input. Which sites count as supported lives in
// ./platforms/allowlist (a copy of the middle-end list); the server re-validates every link.

import { matchAllowlistedPlatform, matchTiktokPost } from './platforms';
import type { SocialPostPlatformId } from './platforms';

export type ClassifyOptions = Readonly<{
	/** Off keeps the hero TikTok-only: other allowlisted sites classify as unsupported. */
	multiInputSources: boolean;
	/** Off makes TikTok links classify as unsupported, like any site off the allowlist. */
	tiktokSource: boolean;
}>;

export type SocialPostClassification =
	| { kind: 'freeform' }
	| { kind: 'supported_link'; platform: SocialPostPlatformId; url: string; handle: string | undefined }
	| { kind: 'unsupported_platform' }
	| { kind: 'invalid_link' };

function hasScheme(input: string): boolean {
	return /^https?:\/\//i.test(input);
}

/** URL-ish means a scheme, or a dotted host before any slash (so a bare word stays freeform). */
function looksLikeUrl(input: string): boolean {
	return hasScheme(input) || /^[^\s/]+\.[^\s/]+/.test(input);
}

function tryParseUrl(candidate: string): URL | undefined {
	try {
		const url = new URL(candidate);
		return url.protocol === 'http:' || url.protocol === 'https:' ? url : undefined;
	} catch {
		return undefined;
	}
}

/** Parse as-is; a scheme-less paste (tiktok.com/@x) is retried with an https prefix. */
function parseUrl(input: string): URL | undefined {
	if (hasScheme(input)) return tryParseUrl(input);
	return tryParseUrl(input) ?? tryParseUrl(`https://${input}`);
}

/**
 * The host-ish prefix of a scheme-less token that failed to parse, so a mangled link still names
 * its site. Anything carrying a scheme is already an invalid link by the time this runs.
 */
function roughHostname(input: string): string {
	return input.split(/[/?#[\]@:]/)[0] ?? '';
}

/**
 * A scheme-less token only counts as a URL when its host ends in a plausible TLD (two or more
 * letters), so dotted prose like "U.S.A" or "e.g." stays freeform. An explicit scheme is
 * always taken at face value.
 */
function hasPlausibleTld(hostname: string): boolean {
	return /\.[a-z]{2,}$/i.test(hostname);
}

/** The link's platform id when the current options accept it. */
function acceptedPlatform(hostname: string, options: ClassifyOptions): SocialPostPlatformId | undefined {
	const platform = matchAllowlistedPlatform(hostname);
	if (!platform) return undefined;
	if (platform.id === 'tiktok') return options.tiktokSource ? platform.id : undefined;
	return options.multiInputSources ? platform.id : undefined;
}

/**
 * A url-ish token that won't parse. An explicit scheme is a link the user meant to paste, so a
 * parse failure is a broken link whatever site the text names; otherwise it only reads as a link
 * when the text names an accepted site.
 */
function classifyUnparseable(trimmed: string, options: ClassifyOptions): SocialPostClassification {
	if (hasScheme(trimmed)) return { kind: 'invalid_link' };
	return acceptedPlatform(roughHostname(trimmed), options) ? { kind: 'invalid_link' } : { kind: 'freeform' };
}

/**
 * Routes raw hero input. A single URL-ish token on an allowlisted site is `supported_link`
 * (normalized to https; TikTok must also be a post shape, else `invalid_link`), any other URL
 * is `unsupported_platform`, and everything else is `freeform`.
 */
export function classifySocialPostInput(input: string, options: ClassifyOptions): SocialPostClassification {
	const trimmed = input.trim();
	if (trimmed.length === 0 || /\s/.test(trimmed) || !looksLikeUrl(trimmed)) {
		return { kind: 'freeform' };
	}

	const url = parseUrl(trimmed);
	if (!url) return classifyUnparseable(trimmed, options);

	if (!hasScheme(trimmed) && !hasPlausibleTld(url.hostname)) {
		return { kind: 'freeform' };
	}

	const platform = acceptedPlatform(url.hostname, options);
	if (!platform) return { kind: 'unsupported_platform' };

	// TikTok also gets a post-shape check, which yields the handle the chip shows.
	let handle: string | undefined;
	if (platform === 'tiktok') {
		const post = matchTiktokPost(url);
		if (!post) return { kind: 'invalid_link' };
		handle = post.handle;
	}

	url.protocol = 'https:';
	return { kind: 'supported_link', platform, url: url.href, handle };
}
